import React from 'react';
import { useTranslation } from 'react-i18next';

import s from './styles.module.scss';
import {
  DAY_ORDER,
  HOURS,
  WORK_END,
  WORK_START,
  cellVisual,
  dayTotal,
  fmt12,
  fmtHour,
  windowsFor,
} from './helpers';

import type { IDayLabel, LimitsMap } from './helpers';

interface IProps {
  limits: LimitsMap;
  dayLabels: IDayLabel[];
}

const ByDayView: React.FC<IProps> = ({ limits, dayLabels }) => {
  const { t } = useTranslation();

  const tickStart = fmt12(WORK_START);
  const tickMid = fmt12(Math.floor((WORK_START + WORK_END) / 2));
  const tickEnd = fmt12(WORK_END);

  return (
    <div className={s.dayGrid}>
      {DAY_ORDER.map((day) => {
        const values = HOURS.map((h) => limits[day]?.[h]);
        const present = values.filter((v) => v !== undefined) as number[];
        const peak = present.length ? Math.max(...present) : 0;
        const cappedCount = present.length;
        const total = dayTotal(limits, day);
        const windowCount = windowsFor(limits[day] ?? {}).length;
        const noun =
          windowCount === 1
            ? t('queueLimits.window')
            : t('queueLimits.windowsPlural');

        return (
          <div className={s.dayCard} key={day}>
            <div className={s.dayCardHeader}>
              <span className={s.dayCardName}>{dayLabels[day].full}</span>
              <span className={s.dayCardMeta}>
                {t('queueLimits.windowCount', { count: windowCount, noun })}
              </span>
            </div>

            <div className={s.strip}>
              {HOURS.map((hour) => {
                const value = limits[day]?.[hour] ?? 0;
                const visual = cellVisual(value);
                return (
                  <div
                    key={hour}
                    className={s.stripCell}
                    style={{ background: visual.bg }}
                    title={`${fmtHour(hour)} · ${value}`}
                  />
                );
              })}
            </div>
            <div className={s.ticks}>
              <span>{tickStart}</span>
              <span>{tickMid}</span>
              <span>{tickEnd}</span>
            </div>

            <div className={s.stats}>
              <div>
                <div className={s.statValue}>{peak.toLocaleString()}</div>
                <div className={s.statLabel}>{t('queueLimits.peakPerHr')}</div>
              </div>
              <div>
                <div className={s.statValue}>{cappedCount}</div>
                <div className={s.statLabel}>
                  {t('queueLimits.hoursCapped')}
                </div>
              </div>
              <div>
                <div className={s.statValue}>{total.toLocaleString()}</div>
                <div className={s.statLabel}>
                  {t('queueLimits.dailyCapacity')}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ByDayView;
